import React from "react";
import s from "./styles.scss";
import { Preview } from "../../../types/IAction";
import {
  Expediente,
  RequestArchivo,
  Archivo,
  StatusAutorizacion,
  CreateDocumentLinkRequest
} from "../expedientsV2controls/types";
import { Styled, ReduxConnect } from "../../../toolkit/decorators/Artifact";
import ArchivoControl from "../expedientsV2controls/archivo/archivo";
import * as actions from "../../actions/expedientes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Container, Row, Col, Button } from "reactstrap";
import {
  successNotification,
  errorNotification
} from "../../../utils/notifications";
import { getPersonByIdRequest } from "../../actions/persons";
import IFrameModal from "../common/iframeModal/iFrameModal";
import ModalFrame from "../../../toolkit/modalFrame";

const mapDispatchToProps = dispatch => ({
  getExpedienteFromTotalPass: (idPersona): Promise<Preview<any>> => {
    return dispatch(actions.getExpedienteFromTotalPass(idPersona));
  },
  postRefresh: (requestArchivo: RequestArchivo): Promise<Preview<any>> => {
    return dispatch(actions.postRefresh(requestArchivo));
  },
  postEnvioCorreo: (idExpediente, idItem): Promise<Preview<any>> => {
    return dispatch(actions.postEnvioCorreo(idExpediente, idItem));
  },
  putCincelFirmas: (idExpediente, idItem): Promise<Preview<any>> => {
    return dispatch(actions.putCincelFirmas(idExpediente, idItem));
  }
});

const mapStateToProps = state => {
  const { expedientes } = state;

  return {
    expedienteTotalPass: expedientes.expedienteTotalPass,
    expedienteTotalPassLoading: expedientes.expedienteTotalPassLoading,
    archivoRefreshLoading: expedientes.archivoRefreshLoading,
    cincelFirmasLoading: expedientes.cincelFirmasLoading
  };
};

interface Props {
  // Datos del expediente y sus archivos
  expedienteTotalPass?: Expediente;
  expedienteTotalPassLoading?: boolean;
  archivoRefreshLoading?: boolean;
  cincelFirmasLoading?: boolean;

  // Actions del expediente
  getExpedienteFromTotalPass?: (idPersona) => Promise<Preview<any>>;
  postRefresh?: (requestArchivo: RequestArchivo) => Promise<Preview<any>>;
  postEnvioCorreo?: (idExpediente, idItem) => Promise<Preview<any>>;
  putCincelFirmas?: (idExpediente, idItem) => Promise<Preview<any>>;

  idPersona: any;
}

interface State {
  waiting: boolean;
  archivos: Archivo[][];

  iFrameModalIsOpen: boolean;
  iFrameModalSrc: string;
  iFrameModalArchiveData: Archivo;

  showFirmanteSelector: boolean;
  firmantes: any[];
  archivoFirmaOnline: Archivo;
}

/**
 * Tabla del módulo de expedientes
 */
@ReduxConnect(mapStateToProps, mapDispatchToProps)
@Styled(s)
export default class ExpedienteTotalPassTabla extends React.Component<
  Props,
  State
> {
  state = {
    waiting: false,
    archivos: [],
    iFrameModalIsOpen: false,
    iFrameModalSrc: null,
    iFrameModalArchiveData: null,
    showFirmanteSelector: false,
    firmantes: null as any[],
    archivoFirmaOnline: null as Archivo
  };

  componentDidMount(): void {
    if (this.props.idPersona) {
      this.props.getExpedienteFromTotalPass(this.props.idPersona);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (this.props.idPersona != prevProps.idPersona) {
      this.props.getExpedienteFromTotalPass(this.props.idPersona);
    }

    // Se activa la animación de carga, cuando se está actualizando el expediente
    if (
      this.props.expedienteTotalPassLoading !=
        prevProps.expedienteTotalPassLoading ||
      this.props.archivoRefreshLoading != prevProps.archivoRefreshLoading ||
      this.props.cincelFirmasLoading != prevProps.cincelFirmasLoading
    ) {
      this.setState({
        waiting:
          this.props.expedienteTotalPassLoading ||
          this.props.archivoRefreshLoading ||
          this.props.cincelFirmasLoading
      });
    }

    // Si se actualiza el expediente correctamente, se ordenan los archivos
    if (
      this.props.expedienteTotalPass != prevProps.expedienteTotalPass &&
      this.props.expedienteTotalPass.vigentes
    ) {
      this.setState({
        archivos: this.ordenarArchivos()
      });
    }
  }

  sendEmail = async (archivo: Archivo) => {
    const preview = await this.props.postEnvioCorreo(
      archivo.idExpediente,
      archivo.idItem
    );
    return preview;
  };

  regenerar = async (archivo: Archivo) => {
    const requestArchivo: RequestArchivo = {
      codigo: archivo.codigo,
      idMembresiaSocio: null,
      idPersonaExpediente: this.props.idPersona,
      idMembresiaSocioPersona: archivo.idMembresiaSocioPersona,
      idPersonaDocumento: archivo.idPersona,
      idMedioPago: archivo.idMedioPago,
      idCotizacionItem: archivo.idCotizacionItem,
      idPersonaEvaluacion: archivo.idPersonaEvaluacion
    };
    const preview = await this.props.postRefresh(requestArchivo);

    if (preview.fulfilled) {
      await this.props.getExpedienteFromTotalPass(this.props.idPersona);
    }

    return preview;
  };

  update = async () => {
    return await this.props.getExpedienteFromTotalPass(this.props.idPersona);
  };

  cincelFirma = async (archivo: Archivo) => {
    if (archivo.statusAutorizacion == StatusAutorizacion.NO_GENERADO) {
      errorNotification(
        "Debe generar el documento antes de verificar las firmas"
      );

      return {
        fulfilled: false
      };
    }

    const preview = await this.props.putCincelFirmas(
      archivo.idExpediente,
      archivo.idItem
    );

    if (preview.fulfilled) {
      successNotification("El documento firmado se obtuvo correctamente");
      await this.props.getExpedienteFromTotalPass(this.props.idPersona);
    }

    return preview;
  };

  firmarOnline = async (archivo: Archivo) => {
    if (archivo.completo) {
      errorNotification("El documento ya se encuentra firmado.");
      return;
    }

    try {
      const { data } = await actions.getCincelDocumentFirmantes(
        archivo.idExpediente,
        archivo.idItem
      );

      const firmantes = ((data || {}).firmantes || []).reverse();

      if (!firmantes || firmantes.length <= 0) {
        errorNotification("No se encontraron firmantes para el documento.");
      } else if (firmantes.length == 1) {
        // Si solo hay un firmante, se firma directamente
        await this.firmarOnlineFirmante(firmantes[0], archivo);
      } else {
        // Si hay más de un firmante, se muestra el selector
        this.setState({
          showFirmanteSelector: true,
          firmantes,
          archivoFirmaOnline: archivo
        });
      }
    } catch (error) {
      errorNotification(
        error && error.response && error.response.data
          ? error.response.data.message || "Error al obtener firmantes"
          : "Error al obtener firmantes"
      );
    }
  };

  firmarOnlineFirmante = async (firmante: any, archivo: Archivo) => {
    try {
      //Se obtienen la info de los firmantes del documento
      console.info("Archivo: ", archivo);

      const archivoPersonaId = archivo.idPersona || archivo.idOwner || null;

      const { data: personaResult } = await getPersonByIdRequest(
        archivoPersonaId
      );
      if (!personaResult) {
        errorNotification("La persona asociada al documento no existe");
        return;
      }

      if ((personaResult.curp || "").length <= 0) {
        errorNotification(
          "La persona no cuenta con un curp válido y registrado"
        );
        return;
      }

      const createCincelDocumentLinkRequest: CreateDocumentLinkRequest = {
        idExpediente: archivo.idExpediente,
        idItem: archivo.idItem,
        idPersona: firmante.idPersona,
        firmanteUuid: firmante.firmanteUuid,
        documentoUuid: firmante.documentoUuid,
        curp: firmante.curp || personaResult.curp || null,
        email: firmante.email,
        nombre: firmante.nombre
      };

      console.info(
        "CreateDocumentLinkRequest: ",
        createCincelDocumentLinkRequest
      );

      const { data: createLinkResult } = await actions.createCincelDocumentLink(
        createCincelDocumentLinkRequest
      );
      console.info("Create link result: ", createLinkResult);
      if (!!createLinkResult && !!createLinkResult.url) {
        //window.open(createLinkResult.url, "_blank");
        this.setState({
          iFrameModalIsOpen: true,
          iFrameModalSrc: createLinkResult.url,
          iFrameModalArchiveData: archivo
        });
        return;
      }
    } catch (error) {
      errorNotification("Error al abrir documento para firma.");
      this.setState({
        iFrameModalIsOpen: false,
        iFrameModalSrc: null,
        iFrameModalArchiveData: null
      });
    }
  };

  ordenarArchivos() {
    const hash: any = {};
    const ordenados = [];

    // Se separan los expedientes por usuario
    for (const archivo of this.props.expedienteTotalPass.vigentes) {
      if (!archivo.moduloTotalPass) {
        continue;
      } else if (hash[archivo.idOwner]) {
        hash[archivo.idOwner].push(archivo);
      } else {
        hash[archivo.idOwner] = [archivo];
      }
    }

    // Se concentran los expedientes por usuario en una lista
    for (const archivosKey in hash) {
      ordenados.push(hash[archivosKey]);
    }

    return ordenados;
  }

  render() {
    // Si no se han cargado o actualizado los documentos, se muestra la animación
    const className =
      this.state.waiting || !this.props.expedienteTotalPass.vigentes
        ? `${s.expedient} ${s.waiting}`
        : s.expedient;

    return (
      <div className={className}>
        <div className={s.spinner}>
          <FontAwesomeIcon icon={faSpinner} pulse />
        </div>

        {this.state.archivos.map(this.renderTable)}
        {this.renderFirmanteSelector()}
      </div>
    );
  }

  renderTable = (archivos: Archivo[]) => {
    if (!archivos || !archivos.length) {
      return <></>;
    }

    // const nombre = `${archivos[0].nombreCompleto} (${archivos[0].idOwner})`;
    return (
      <>
        <IFrameModal
          isOpen={this.state.iFrameModalIsOpen}
          title={"Firma de documento"}
          okTitle={"Procesar firmas"}
          ok={() => {
            const { iFrameModalArchiveData } = this.state;
            this.cincelFirma({ ...iFrameModalArchiveData });
            this.setState({
              iFrameModalSrc: null,
              iFrameModalIsOpen: false,
              iFrameModalArchiveData: null
            });
          }}
          cancel={() => {
            this.setState({
              iFrameModalSrc: null,
              iFrameModalIsOpen: false,
              iFrameModalArchiveData: null
            });
          }}
          src={this.state.iFrameModalSrc}
        />

        <div className={s.space}></div>
        <div style={{ width: "400px" }}>
          <Button
            className="primaryButton"
            onClick={async () => {
              this.setState({ waiting: true });

              try {
                await actions.putVerificarCondicinalesTotalPass(
                  this.props.idPersona
                );
                this.props.getExpedienteFromTotalPass(this.props.idPersona);
                this.setState({ waiting: false });
              } catch (err) {
                this.setState({ waiting: false });
                errorNotification("No se pudieron verificar los documentos");
              }
            }}
            size="sm"
            block
            disabled={this.state.waiting}
          >
            Verificar condicionales
          </Button>
        </div>
        <div className={s.space}></div>
        {archivos.map(archivo => (
          <ArchivoControl
            key={"archivo-item-" + archivo.idItem}
            archivo={archivo}
            userId={0}
            sendEmail={this.sendEmail}
            regenerar={this.regenerar}
            update={this.update}
            cincelFirma={this.cincelFirma}
            firmarOnline={this.firmarOnline}
          />
        ))}
      </>
    );
  };

  renderFirmanteSelector = () => {
    if (!this.state.firmantes) {
      return null;
    }

    const archivo = this.state.archivoFirmaOnline;

    return (
      <ModalFrame
        title={"Seleccionar firmante"}
        isOpen={this.state.showFirmanteSelector}
        toggle={() => this.setState({ showFirmanteSelector: false })}
        size="md"
      >
        <Container style={{ width: "90%" }} className="center-block p-0 mb-3">
          <Row>
            <div
              style={{
                backgroundColor: "#eee",
                fontSize: "11px",
                fontWeight: "bold",
                padding: "4px",
                width: "100%",
                textAlign: "center"
              }}
            >
              Recuerde que las firmas deben solicitarse en cierto orden.
            </div>
          </Row>
          {this.state.firmantes.map(firmante => (
            <Row key={firmante.firmanteUuid} className="mt-2 mb-3">
              <Col xs="12" md="12">
                <div
                  style={{
                    padding: "4px",
                    borderRadius: "5px",
                    backgroundColor: "#fff2cc"
                  }}
                >
                  Firmante: {firmante.nombre}
                  <br />
                  <span style={{ fontSize: "12px" }}>{firmante.email}</span>
                </div>
              </Col>
              <Col xs="12" md="12">
                <Button
                  className={`${s.secundaryButton} mt-3`}
                  onClick={() => {
                    this.firmarOnlineFirmante(firmante, archivo);
                    this.setState({ showFirmanteSelector: false });
                  }}
                  block
                  disabled={this.state.waiting || archivo.completo}
                >
                  Firmar
                </Button>
              </Col>
              <Col xs="12" md="12">
                <hr />
              </Col>
            </Row>
          ))}

          <Row className="mt-2">
            <Col xs="12" md="12">
              <Button
                className={`${s.secundaryButton} mt-3`}
                onClick={() => {
                  this.setState({ showFirmanteSelector: false });
                }}
                block
                disabled={this.state.waiting}
              >
                Cancelar
              </Button>
            </Col>
          </Row>
        </Container>
      </ModalFrame>
    );
  };
}
